"use client";
import React, { useState, useEffect, useMemo, useRef } from "react";
// import { Menu, X } from 'lucide-react';
import { useSelector, useDispatch } from "react-redux";
import { useModal } from "../../Modals/ModalContext";
// Import components
import HeaderLogo from "./HeaderLogo";
import AIStatusIndicator from "./AIStatusIndicator";
// import DesktopActions from './DesktopActions';
import WalletConnectButton from "./WalletConnectButton";
import ConnectedWalletDisplay from "./ConnectedWalletDisplay";
import UserProfileDropdown from "./UserProfileDropdown";
import MobileProfileButton from "./MobileProfileButton";
import MobileMenu from "./MobileMenu";
import DisconnectWalletModal from "./Modals/DisconnectWalletModal";
import LogoutModal from "./Modals/LogoutModal";

// Import custom hook
import useHeaderData from "./hooks/useHeaderData.jsx";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { setIsCollapsed, setIsMobileSidebarOpen } from "@/redux/slices/sidebarSlice";
import {
  PanelLeftClose,
  PanelLeftOpen,
  Bell,
} from "lucide-react";
import { getEngineById } from "@/config/engines";
import { IDX_LAUNCHLAB_LIVE_PATH } from "@/config/idxLaunchlabAppConfig";
import { IDX_EXCHANGE_URL } from "@/config/idxExchangeAppConfig";
import { SMART_SELL_NAV_ENABLED } from "@/config/smartToolsLandingConfig";

const SMART_SELL_NOTIFICATION_ID = "smart-sell-intro-v1";

const HIDDEN_NOTIFICATION_IDS = new Set([
  "recommended-strategy-v8-mini",
  "minibuy-gas-monitoring-v2",
  "minibuy-gas-monitoring",
  "volume-bot-new-portfolio-combo-v1",
  ...(!SMART_SELL_NAV_ENABLED ? [SMART_SELL_NOTIFICATION_ID] : []),
]);

const LAUNCHLAB_NOTIFICATION_ID = "idx-launchlab-intro-v1";
const EXCHANGE_NOTIFICATION_ID = "idx-exchange-intro-v1";

const VOLUME_BOT_NEW_V12_NOTIFICATION_ID = "volume-bot-new-v12-v1";
const VOLUME_BOT_NEW_V10_NOTIFICATION_ID = "volume-bot-new-v10-v1";
const VOLUME_BOT_NEW_PAIR_NOTIFICATION_ID = "volume-bot-new-pair-v1";

function filterVisibleNotifications(items) {
  if (!Array.isArray(items)) return [];
  return items.filter(
    (item) => item?.id && !HIDDEN_NOTIFICATION_IDS.has(item.id),
  );
}

const NOTIFICATION_PANEL_TRANSITION_MS = 120;

export default function AutobotHeader() {
  const NOTIFICATION_STORAGE_KEY = "idx_header_notifications_v1";
  const PENDING_POST_AUTH_ACTION_KEY = "idx_pending_post_auth_action";
  const PENDING_ACTION_OPEN_REFERRAL = "open_referral";
  const PENDING_ACTION_TTL_MS = 2 * 60 * 1000;
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { isMobileSidebarOpen } = useSelector((state) => state.sidebar);
  const dispatch = useDispatch();
  const { openModal, openReferralModal } = useModal();
  const {
    // State
    showWalletDropdown,
    setShowWalletDropdown,
    showProfileDropdown,
    setShowProfileDropdown,
    showMobileMenu,
    // setShowMobileMenu,
    // notifications,
    autobotStatus,
    showLogoutModal,
    setShowLogoutModal,
    showDisconnectWalletModal,
    setShowDisconnectWalletModal,
    userDataFromLocal,
    solBalance,
    isLoadingSolBalance,
    solBalanceError,
    networks,
    walletProviders,
    // Wallet state
    isWalletConnected,
    walletAddress,
    connectedWallet,
    selectedNetwork,
    isConnecting,
    walletError,
    userProfile,
    // Functions
    handleLogout,
    getAutobotStatusColor,
    getInitials,
    selectedNetworkData,
    getWalletStatus,
    refreshSolBalance,
    handleCopyAddress,
    handleWalletConnection,

    // Wallet functions
    disconnectWallet,
    formatAddress,
    detectWallets,
    clearError,
  } = useHeaderData();
  const pathname = usePathname();
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notificationRendered, setNotificationRendered] = useState(false);
  const [notificationEntered, setNotificationEntered] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notificationRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || typeof window === "undefined") return;
    const pendingRaw = window.sessionStorage.getItem(
      PENDING_POST_AUTH_ACTION_KEY,
    );
    if (!pendingRaw) return;
    window.sessionStorage.removeItem(PENDING_POST_AUTH_ACTION_KEY);

    let pendingAction = null;
    try {
      const parsed = JSON.parse(pendingRaw);
      if (parsed && typeof parsed === "object") {
        pendingAction = parsed;
      }
    } catch (_) {
      // Legacy raw-string entries are treated as stale and ignored.
      pendingAction = null;
    }
    if (!pendingAction) return;

    const action = String(pendingAction.action || "");
    const source = String(pendingAction.source || "");
    const createdAt = Number(pendingAction.createdAt || 0);
    const isFresh =
      Number.isFinite(createdAt) &&
      createdAt > 0 &&
      Date.now() - createdAt <= PENDING_ACTION_TTL_MS;

    if (
      action === PENDING_ACTION_OPEN_REFERRAL &&
      source === "refer_click" &&
      isFresh
    ) {
      openReferralModal();
    }
  }, [isAuthenticated, openReferralModal, PENDING_ACTION_TTL_MS]);

  useEffect(() => {
    const exchangeNotification = {
      id: EXCHANGE_NOTIFICATION_ID,
      title: "IDX Exchange",
      message:
        "Trade on IDX Exchange — BTC, ETH, and SOL perps with up to 100x leverage, spot Swap, and multi-chain access on Solana, Base, Arbitrum & more at dex.idxsolana.io.",
      createdAt: Date.now() + 11,
      read: false,
      isNew: true,
    };
    const launchlabNotification = {
      id: LAUNCHLAB_NOTIFICATION_ID,
      title: "IDX Launchlab",
      message:
        "Launch on a bonding curve — browse the live feed, create a coin, and claim creator rewards.",
      createdAt: Date.now() + 10,
      read: false,
      isHot: true,
      isNew: true,
    };
    const communitySignalNotification = {
      id: "community-signal-intro",
      title: "Community Signal",
      message:
        "For launch event date updates, open Community Signal from your token bot card in Dashboard.",
      createdAt: Date.now() + 4,
      read: false,
    };
    const fastSwapNotification = {
      id: "idx-fast-swap-intro",
      title: "Fast Swap",
      message:
        "Don't miss volatile moves — swap routes through Jupiter aggregation right in IDX.",
      createdAt: Date.now() + 5,
      read: false,
    };
    const botCardStatsNotification = {
      id: "bot-card-settings-stats-v1",
      title: "Dashboard bot stats",
      message:
        "Open Bot settings on a bot card to see volume (~USD), transaction count, and activity since creation.",
      createdAt: Date.now() + 6,
      read: false,
    };
    const aiAssistanceNotification = {
      id: "autobot-ai-assistance-intro",
      title: "AI Assistance",
      message:
        "open AI Assistance (bottom-right) for recharge, start/stop, gas, and error help.",
      createdAt: Date.now() + 7,
      read: false,
    };
    const volumeBotNewV10Notification = {
      id: VOLUME_BOT_NEW_V10_NOTIFICATION_ID,
      title: "V10 engine launch",
      message:
        "Engine V10 is live — ~3000 SOL/day, unique high tx, continuous trades, ~1 SOL/hour.",
      createdAt: Date.now() + 12,
      read: false,
      isNew: true,
    };
    const volumeBotNewV12Notification = {
      id: VOLUME_BOT_NEW_V12_NOTIFICATION_ID,
      title: "V12 engine launch",
      message:
        "Try the new V12 engine — high-visibility rankings, ~15000 SOL/day, 19000+ makers, and instant trades. Pair with Mini Buy/Sell for strong ranking setups.",
      createdAt: Date.now() + 9,
      read: false,
      isNew: true,
    };
    const volumeBotNewV4EcoNotification = {
      id: "volume-bot-new-v4-eco-v1",
      title: "V4 Eco engine launch",
      message:
        "Try the new V4 Eco engine — lower hourly rate, extra gas savings, and a cost-smart way to boost your coin.",
      createdAt: Date.now() + 8,
      read: false,
    };
    const volumeBotNewPairNotification = {
      id: VOLUME_BOT_NEW_PAIR_NOTIFICATION_ID,
      title: "Pair engine launch",
      message:
        "Pair bot is live — trade your second token and save cost ~50%.",
      createdAt: Date.now() + 14,
      read: false,
      isNew: true,
    };
    const smartSellNotification = {
      id: SMART_SELL_NOTIFICATION_ID,
      title: "Smart Sell",
      message:
        "Sell a slice of each real buy above your target market cap — from a deposit bag, through fresh wallets.",
      createdAt: Date.now() + 13,
      read: false,
      isNew: true,
    };

    const upsertNotification = (list, notification) => {
      const idx = list.findIndex((item) => item.id === notification.id);
      if (idx >= 0) {
        list[idx] = {
          ...list[idx],
          title: notification.title,
          message: notification.message,
        };
        return list;
      }
      list.unshift(notification);
      return list;
    };

    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(NOTIFICATION_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const merged = filterVisibleNotifications(
            parsed.filter(
              (item) =>
                item.id !== "welcome-notice" &&
                item.id !== "dashboard-bot-cards-fix",
            ),
          );
          if (
            !merged.some((item) => item.id === communitySignalNotification.id)
          ) {
            merged.unshift(communitySignalNotification);
          }
          if (!merged.some((item) => item.id === fastSwapNotification.id)) {
            merged.unshift(fastSwapNotification);
          }
          if (
            !merged.some((item) => item.id === botCardStatsNotification.id)
          ) {
            merged.unshift(botCardStatsNotification);
          }
          upsertNotification(merged, volumeBotNewV10Notification);
          upsertNotification(merged, volumeBotNewV12Notification);
          upsertNotification(merged, volumeBotNewV4EcoNotification);
          upsertNotification(merged, volumeBotNewPairNotification);
          upsertNotification(merged, aiAssistanceNotification);
          upsertNotification(merged, exchangeNotification);
          upsertNotification(merged, launchlabNotification);
          upsertNotification(merged, smartSellNotification);
          const cleaned = orderNotifications(
            merged.map((item) =>
              item.id === LAUNCHLAB_NOTIFICATION_ID ||
              item.id === EXCHANGE_NOTIFICATION_ID ||
              item.id === VOLUME_BOT_NEW_V12_NOTIFICATION_ID ||
              item.id === VOLUME_BOT_NEW_V10_NOTIFICATION_ID ||
              item.id === VOLUME_BOT_NEW_PAIR_NOTIFICATION_ID ||
              item.id === SMART_SELL_NOTIFICATION_ID
                ? item
                : { ...item, isNew: false, isHot: false },
            ),
          );
          setNotifications(cleaned);
          try {
            window.localStorage.setItem(
              NOTIFICATION_STORAGE_KEY,
              JSON.stringify(cleaned),
            );
          } catch {
            // Ignore localStorage write errors.
          }
          return;
        }
      }
    } catch {
      // Ignore localStorage read errors.
    }

    // Simple default notification system seed.
    setNotifications(
      orderNotifications([
        smartSellNotification,
        volumeBotNewPairNotification,
        exchangeNotification,
        launchlabNotification,
        volumeBotNewV10Notification,
        volumeBotNewV12Notification,
        volumeBotNewV4EcoNotification,
        aiAssistanceNotification,
        fastSwapNotification,
        communitySignalNotification,
        botCardStatsNotification,
      ]),
    );
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !mounted) return;
    try {
      window.localStorage.setItem(
        NOTIFICATION_STORAGE_KEY,
        JSON.stringify(filterVisibleNotifications(notifications)),
      );
    } catch {
      // Ignore localStorage write errors.
    }
  }, [notifications, mounted]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setIsNotificationOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (isNotificationOpen) {
      setNotificationRendered(true);
      setNotificationEntered(false);
      let frame2 = 0;
      const frame1 = requestAnimationFrame(() => {
        frame2 = requestAnimationFrame(() => {
          setNotificationEntered(true);
        });
      });
      return () => {
        cancelAnimationFrame(frame1);
        cancelAnimationFrame(frame2);
      };
    }

    setNotificationEntered(false);
    const exitTimer = setTimeout(
      () => setNotificationRendered(false),
      NOTIFICATION_PANEL_TRANSITION_MS,
    );
    return () => clearTimeout(exitTimer);
  }, [isNotificationOpen]);

  const visibleNotifications = useMemo(
    () => filterVisibleNotifications(notifications),
    [notifications],
  );

  const unreadCount = useMemo(
    () => visibleNotifications.filter((item) => !item.read).length,
    [visibleNotifications],
  );

  const openNotifications = () => {
    setIsNotificationOpen((prev) => !prev);
    if (unreadCount > 0) {
      setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
    }
  };

  const acknowledgeNotification = (notificationId) => {
    if (!notificationId) return;
    setNotifications((prev) =>
      prev.map((item) =>
        item.id === notificationId
          ? { ...item, read: true, isNew: false, isHot: false }
          : item,
      ),
    );
  };

  const COMMUNITY_SIGNAL_NOTIFICATION_ID = "community-signal-intro";
  const FAST_SWAP_NOTIFICATION_ID = "idx-fast-swap-intro";
  const BOT_CARD_STATS_NOTIFICATION_ID = "bot-card-settings-stats-v1";
  const VOLUME_BOT_NEW_V4ECO_NOTIFICATION_ID = "volume-bot-new-v4-eco-v1";
  const AI_ASSISTANCE_NOTIFICATION_ID = "autobot-ai-assistance-intro";
  const VOLUME_BOT_ENGINE_LAUNCH_NOTIFICATIONS = {
    [VOLUME_BOT_NEW_V10_NOTIFICATION_ID]: "V10",
    [VOLUME_BOT_NEW_V12_NOTIFICATION_ID]: "V12",
    [VOLUME_BOT_NEW_V4ECO_NOTIFICATION_ID]: "V4Eco",
    [VOLUME_BOT_NEW_PAIR_NOTIFICATION_ID]: "Pair",
  };

  const orderNotifications = (items) => {
    const priority = {
      [SMART_SELL_NOTIFICATION_ID]: 0,
      [VOLUME_BOT_NEW_PAIR_NOTIFICATION_ID]: 1,
      [VOLUME_BOT_NEW_V10_NOTIFICATION_ID]: 2,
      [VOLUME_BOT_NEW_V12_NOTIFICATION_ID]: 3,
      [EXCHANGE_NOTIFICATION_ID]: 4,
      [LAUNCHLAB_NOTIFICATION_ID]: 5,
      [VOLUME_BOT_NEW_V4ECO_NOTIFICATION_ID]: 6,
      [AI_ASSISTANCE_NOTIFICATION_ID]: 7,
      [FAST_SWAP_NOTIFICATION_ID]: 8,
      [COMMUNITY_SIGNAL_NOTIFICATION_ID]: 9,
      [BOT_CARD_STATS_NOTIFICATION_ID]: 10,
    };
    return [...items].sort((a, b) => {
      const pa = priority[a?.id] ?? 10;
      const pb = priority[b?.id] ?? 10;
      if (pa !== pb) return pa - pb;
      return Number(b?.createdAt || 0) - Number(a?.createdAt || 0);
    });
  };

  const goToVolumeBotWithEngine = (engineId, notificationId) => {
    acknowledgeNotification(notificationId);
    setIsNotificationOpen(false);
    const qs = new URLSearchParams({
      engine: engineId,
      dl: String(Date.now()),
    });
    const path =
      String(engineId) === "Pair" ? "/pair-bot" : "/volume-bot";
    router.push(`${path}?${qs.toString()}#create-bot-step-2-engines`, {
      scroll: false,
    });
  };

  const goToCommunityMarketingPage = (notificationId) => {
    acknowledgeNotification(notificationId);
    setIsNotificationOpen(false);
    router.push("/community");
  };

  const openAiAssistance = (notificationId) => {
    acknowledgeNotification(notificationId);
    setIsNotificationOpen(false);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("idx:open-ai-support"));
    }
  };

  // Tool pages: show wallet connect even when user is not logged in (wallet-only flow)
  const isTokenDexHubPage =
    pathname === "/token" || pathname.startsWith("/token/");
  const toolPagePrefixes = [
    "/dashboard",
    "/",
    "/volume-bot",
    "/pair-bot",
    "/holders-maker",
    "/smart-tools",
    "/smart-sell",
    "/reaction-booster",
    "/token-hub",
    "/token-burn",
    "/update-metadata",
    "/bulk-sender/solana",
    "/token-creator",
    "/crypto-creator",
    "/crypto-token-creator",
    "/liquidity-pool-creator",
    "/fast-swap",
    "/create-openbook-market",
    "/lock-liquidity",
    "/lock-certificate",
    "/revoke-mint-authority",
    "/revoke-freeze",
    "/manage-liquidity",
    "/token-creator/token-2022",
    "/revive",
    "/revivals",
    "/community",
    "/idx-launchlab",
  ];
  const isToolPage =
    isTokenDexHubPage ||
    toolPagePrefixes.some((prefix) =>
      prefix === "/" ? pathname === "/" : pathname.startsWith(prefix),
    );

  const showWalletSection = isToolPage;

  // Defer client-dependent UI until after mount to avoid hydration mismatch (auth/wallet can differ on server vs client)
  const rightSection = !mounted ? (
    <div
      className="hidden h-9 w-24 flex-shrink-0 items-center space-x-2 sm:space-x-3 lg:flex"
      aria-hidden
    />
  ) : (
    <>
      {/* Wallet Section: on tool pages show wallet; on token-creator also when not logged in */}
      {showWalletSection && !isWalletConnected ? (
        <>
          <div className="shrink-0">
            <WalletConnectButton
              showWalletDropdown={showWalletDropdown}
              setShowWalletDropdown={setShowWalletDropdown}
              isConnecting={isConnecting}
              walletError={walletError}
              clearError={clearError}
              walletProviders={walletProviders}
              handleWalletConnection={handleWalletConnection}
              detectWallets={detectWallets}
              getWalletStatus={getWalletStatus}
            />
          </div>
        </>
      ) : showWalletSection && isWalletConnected ? (
        <>
          <ConnectedWalletDisplay
            walletAddress={walletAddress}
            formatAddress={formatAddress}
            connectedWallet={connectedWallet}
            solBalance={solBalance}
            isLoadingSolBalance={isLoadingSolBalance}
            solBalanceError={solBalanceError}
            selectedNetworkData={selectedNetworkData}
            refreshSolBalance={refreshSolBalance}
            handleCopyAddress={handleCopyAddress}
            disconnectWallet={disconnectWallet}
          />
        </>
      ) : null}

      {/* Authentication Section - Always show based on auth status */}
      {!isAuthenticated ? (
        <div className="flex items-center space-x-1.5 lg:space-x-2">
          <button
            onClick={() => openModal("login")}
            className="px-2.5 py-1.5 text-xs font-medium text-gray-300 hover:text-white transition-colors lg:px-4 lg:py-2 lg:text-sm"
          >
            Login
          </button>
          <button
            onClick={() => openModal("register")}
            className="px-2.5 py-1.5 text-xs font-medium bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors lg:rounded-lg lg:px-4 lg:py-2 lg:text-sm"
          >
            Sign Up
          </button>
        </div>
      ) : (
        <>
          <div className="relative shrink-0" ref={notificationRef}>
            <button
              type="button"
              onClick={openNotifications}
              className="relative shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-800/60 hover:text-white sm:p-2"
              aria-label="Open notifications"
              aria-expanded={isNotificationOpen}
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
              )}
            </button>

            {notificationRendered && (
              <div
                className={`fixed right-3 left-auto top-16 z-[120] w-[min(20rem,calc(100vw-1.5rem))] max-w-[calc(100vw-1.5rem)] transition-all ease-out duration-150 lg:absolute lg:right-0 lg:top-full lg:left-auto lg:mt-0 lg:w-auto lg:max-w-none lg:pt-5 lg:origin-top-right ${
                  notificationEntered
                    ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
                    : "pointer-events-none -translate-y-1 scale-[0.98] opacity-0"
                }`}
              >
                <div className="overflow-hidden rounded-xl border border-gray-700 bg-gray-900 shadow-2xl lg:w-80 lg:max-w-[85vw]">
                <div className="px-4 py-3 border-b border-gray-800">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-white">
                      Notifications
                    </p>
                    <span className="text-xs text-gray-400">Latest updates</span>
                  </div>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {visibleNotifications.length === 0 ? (
                    <div className="px-4 py-6 text-sm text-gray-400 text-center">
                      No notifications
                    </div>
                  ) : (
                    visibleNotifications.map((item) =>
                      item.id === SMART_SELL_NOTIFICATION_ID ? (
                        <Link
                          key={item.id}
                          href="/smart-sell"
                          prefetch={false}
                          onClick={() => {
                            acknowledgeNotification(item.id);
                            setIsNotificationOpen(false);
                          }}
                          className="block w-full px-4 py-3 border-b border-gray-800/80 last:border-b-0 text-left transition-colors hover:bg-gray-800/55 focus-visible:bg-gray-800/55 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cyan-500/60"
                          rel="noopener noreferrer"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-emerald-400/80"
                              aria-hidden="true"
                            />
                            <p className="inline-flex flex-wrap items-center gap-1.5 text-xs font-normal text-white">
                              <span>{item.title}</span>
                              <span className="rounded px-1 py-px text-[8px] font-semibold uppercase tracking-wide text-emerald-400">
                                Beta
                              </span>
                            </p>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {item.message}
                          </p>
                        </Link>
                      ) : item.id === EXCHANGE_NOTIFICATION_ID ? (
                        <a
                          key={item.id}
                          href={IDX_EXCHANGE_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            acknowledgeNotification(item.id);
                            setIsNotificationOpen(false);
                          }}
                          className="block w-full px-4 py-3 border-b border-gray-800/80 last:border-b-0 text-left transition-colors hover:bg-gray-800/55 focus-visible:bg-gray-800/55 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cyan-500/60"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-emerald-400/80"
                              aria-hidden="true"
                            />
                            <p className="inline-flex flex-wrap items-center gap-1.5 text-xs font-normal text-white">
                              {item.title}
                            </p>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {item.message}
                          </p>
                        </a>
                      ) : item.id === LAUNCHLAB_NOTIFICATION_ID ? (
                        <Link
                          key={item.id}
                          href={IDX_LAUNCHLAB_LIVE_PATH}
                          prefetch={false}
                          onClick={() => {
                            acknowledgeNotification(item.id);
                            setIsNotificationOpen(false);
                          }}
                          className="block w-full px-4 py-3 border-b border-gray-800/80 last:border-b-0 text-left transition-colors hover:bg-gray-800/55 focus-visible:bg-gray-800/55 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cyan-500/60"
                          rel="noopener noreferrer"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-emerald-400/80"
                              aria-hidden="true"
                            />
                            <p className="inline-flex flex-wrap items-center gap-1.5 text-xs font-normal text-white">
                              {item.title}
                            </p>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {item.message}
                          </p>
                        </Link>
                      ) : item.id === COMMUNITY_SIGNAL_NOTIFICATION_ID ? (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => goToCommunityMarketingPage(item.id)}
                          className="w-full px-4 py-3 border-b border-gray-800/80 last:border-b-0 text-left transition-colors hover:bg-gray-800/55 focus-visible:bg-gray-800/55 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cyan-500/60"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-green-400/80"
                              aria-hidden="true"
                            />
                            <p className="inline-flex flex-wrap items-center gap-1.5 text-xs font-normal text-white">
                              {item.title}
                            </p>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {item.message}
                          </p>
                        </button>
                      ) : item.id === FAST_SWAP_NOTIFICATION_ID ? (
                        <Link
                          key={item.id}
                          href="/fast-swap"
                          prefetch={false}
                          onClick={() => {
                            acknowledgeNotification(item.id);
                            setIsNotificationOpen(false);
                          }}
                          className="block w-full px-4 py-3 border-b border-gray-800/80 last:border-b-0 text-left transition-colors hover:bg-gray-800/55 focus-visible:bg-gray-800/55 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cyan-500/60"
                          rel="noopener noreferrer"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-green-400/80"
                              aria-hidden="true"
                            />
                            <p className="inline-flex flex-wrap items-center gap-1.5 text-xs font-normal text-white">
                              {item.title}
                            </p>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {item.message}
                          </p>
                        </Link>
                      ) : item.id === BOT_CARD_STATS_NOTIFICATION_ID ? (
                        <Link
                          key={item.id}
                          href="/dashboard?openTradingBotSettings=1"
                          prefetch={false}
                          onClick={() => {
                            acknowledgeNotification(item.id);
                            setIsNotificationOpen(false);
                          }}
                          className="block w-full px-4 py-3 border-b border-gray-800/80 last:border-b-0 text-left transition-colors hover:bg-gray-800/55 focus-visible:bg-gray-800/55 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cyan-500/60"
                          rel="noopener noreferrer"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-green-400/80"
                              aria-hidden="true"
                            />
                            <p className="inline-flex flex-wrap items-center gap-1.5 text-xs font-normal text-white">
                              {item.title}
                            </p>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {item.message}
                          </p>
                        </Link>
                      ) : item.id === AI_ASSISTANCE_NOTIFICATION_ID ? (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => openAiAssistance(item.id)}
                          className="w-full px-4 py-3 border-b border-gray-800/80 last:border-b-0 text-left transition-colors hover:bg-gray-800/55 focus-visible:bg-gray-800/55 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cyan-500/60"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-emerald-400/80"
                              aria-hidden="true"
                            />
                            <p className="inline-flex flex-wrap items-center gap-1.5 text-xs font-normal text-white">
                              {item.title}
                            </p>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {item.message}
                          </p>
                        </button>
                      ) : VOLUME_BOT_ENGINE_LAUNCH_NOTIFICATIONS[item.id] ? (
                        <Link
                          key={item.id}
                          href={`${
                            VOLUME_BOT_ENGINE_LAUNCH_NOTIFICATIONS[item.id] ===
                            "Pair"
                              ? "/pair-bot"
                              : "/volume-bot"
                          }?engine=${VOLUME_BOT_ENGINE_LAUNCH_NOTIFICATIONS[item.id]}`}
                          prefetch={false}
                          scroll={false}
                          onClick={(e) => {
                            e.preventDefault();
                            goToVolumeBotWithEngine(
                              VOLUME_BOT_ENGINE_LAUNCH_NOTIFICATIONS[item.id],
                              item.id,
                            );
                          }}
                          className="block w-full px-4 py-3 border-b border-gray-800/80 last:border-b-0 text-left transition-colors hover:bg-gray-800/55 focus-visible:bg-gray-800/55 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cyan-500/60"
                          rel="noopener noreferrer"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-green-400/80"
                              aria-hidden="true"
                            />
                            <p className="inline-flex flex-wrap items-center gap-1.5 text-xs font-normal text-white">
                              <span>{item.title}</span>
                              {item.id === VOLUME_BOT_NEW_V10_NOTIFICATION_ID ||
                              item.id === VOLUME_BOT_NEW_PAIR_NOTIFICATION_ID ? (
                                <span className="rounded px-1 py-px text-[8px] font-semibold uppercase tracking-wide text-emerald-400">
                                  New
                                </span>
                              ) : null}
                              {(() => {
                                const launchIcon = getEngineById(
                                  VOLUME_BOT_ENGINE_LAUNCH_NOTIFICATIONS[
                                    item.id
                                  ],
                                )?.icon;
                                return launchIcon ? (
                                  <span
                                    className="text-sm leading-none select-none"
                                    aria-hidden
                                  >
                                    {launchIcon}
                                  </span>
                                ) : null;
                              })()}
                            </p>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {item.message}
                          </p>
                        </Link>
                      ) : null,
                    )
                  )}
                </div>
                </div>
              </div>
            )}
          </div>
          <div className="hidden lg:flex items-center space-x-3">
            <UserProfileDropdown
              userProfile={userProfile}
              userDataFromLocal={userDataFromLocal}
              walletAddress={walletAddress}
              formatAddress={formatAddress}
              connectedWallet={connectedWallet}
              solBalance={solBalance}
              isLoadingSolBalance={isLoadingSolBalance}
              solBalanceError={solBalanceError}
              selectedNetworkData={selectedNetworkData}
              getInitials={getInitials}
              setShowDisconnectWalletModal={
                setShowDisconnectWalletModal
              }
              setShowLogoutModal={setShowLogoutModal}
            />
          </div>

          <MobileProfileButton
            showProfileDropdown={showProfileDropdown}
            setShowProfileDropdown={setShowProfileDropdown}
            userProfile={userProfile}
            userDataFromLocal={userDataFromLocal}
            walletAddress={walletAddress}
            connectedWallet={connectedWallet}
            solBalance={solBalance}
            isLoadingSolBalance={isLoadingSolBalance}
            solBalanceError={solBalanceError}
            selectedNetworkData={selectedNetworkData}
            getInitials={getInitials}
            handleCopyAddress={handleCopyAddress}
            setShowDisconnectWalletModal={setShowDisconnectWalletModal}
            setShowLogoutModal={setShowLogoutModal}
          />
        </>
      )}
    </>
  );

  return (
    <>
      {/* Fixed Header Container */}
      <div className="fixed top-0 left-0 right-0 z-[160] bg-gray-950/95 backdrop-blur-xl border-b border-gray-800">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        <div className="relative">
          {/* Main Header Row */}
          <div className="flex h-14 min-w-0 items-center gap-1 pl-0 pr-2 sm:h-16 sm:gap-2 sm:pr-4 lg:pr-6">
            <div className="flex shrink-0 items-center gap-0.5 sm:gap-2">
              <div className="flex w-11 shrink-0 items-center justify-center sm:w-12 lg:w-16">
                <button
                  onClick={() => {
                    const nextOpen = !isMobileSidebarOpen;
                    dispatch(setIsMobileSidebarOpen(nextOpen));
                    dispatch(setIsCollapsed(!nextOpen));
                  }}
                  className="p-2 text-gray-300 hover:text-white rounded-lg hover:bg-gray-800/60"
                  aria-label={isMobileSidebarOpen ? "Collapse menu" : "Expand menu"}
                >
                  {isMobileSidebarOpen ? (
                    <PanelLeftClose className="w-6 h-6 stroke-[1.5]" />
                  ) : (
                    <PanelLeftOpen className="w-6 h-6 stroke-[1.5]" />
                  )}
                </button>
              </div>
              <HeaderLogo />
            </div>
            {/* Left Section - Logo & Brand */}

            {/* Center Section - AI Status (Desktop Only) */}
            <AIStatusIndicator isAuthenticated={isAuthenticated} />

            {/* Right Section - Actions & Profile (deferred until mounted to avoid hydration mismatch) */}
            <div className="ml-auto flex shrink-0 items-center gap-0.5 sm:gap-2 lg:gap-3">
              {rightSection}
            </div>
          </div>

          {/* Mobile Menu */}
          <MobileMenu
            showMobileMenu={showMobileMenu}
            userProfile={userProfile}
            userDataFromLocal={userDataFromLocal}
            getInitials={getInitials}
            isWalletConnected={isWalletConnected}
            walletAddress={walletAddress}
            formatAddress={formatAddress}
            connectedWallet={connectedWallet}
            solBalance={solBalance}
            isLoadingSolBalance={isLoadingSolBalance}
            solBalanceError={solBalanceError}
            selectedNetworkData={selectedNetworkData}
            refreshSolBalance={refreshSolBalance}
            selectedNetwork={selectedNetwork}
            networks={networks}
            // notifications={notifications}
            autobotStatus={autobotStatus}
            getAutobotStatusColor={getAutobotStatusColor}
            setShowLogoutModal={setShowLogoutModal}
            isAuthenticated={isAuthenticated}
          />
        </div>
      </div>

      {/* Disconnect Wallet Confirmation Modal */}
      <DisconnectWalletModal
        showDisconnectWalletModal={showDisconnectWalletModal}
        setShowDisconnectWalletModal={setShowDisconnectWalletModal}
        walletAddress={walletAddress}
        solBalance={solBalance}
        isLoadingSolBalance={isLoadingSolBalance}
        solBalanceError={solBalanceError}
        selectedNetworkData={selectedNetworkData}
        disconnectWallet={disconnectWallet}
      />

      {/* Logout Confirmation Modal */}
      <LogoutModal
        showLogoutModal={showLogoutModal}
        setShowLogoutModal={setShowLogoutModal}
        handleLogout={handleLogout}
      />
    </>
  );
}
